import { useState, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Plus, X } from 'lucide-react'
import { api } from '../services/api'
import Button from './Button'
import Input from './Input'

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
}

export default function PostTranslationBanner({
  post,
  locale,
  languageName,
  onTranslationCreated,
}) {
  const [showModal, setShowModal] = useState(false)
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [error, setError] = useState('')
  const slugManuallyEdited = useRef(false)
  const queryClient = useQueryClient()

  const createTranslationMutation = useMutation({
    mutationFn: (data) =>
      api.post(`/posts/${post.id}/translations/${locale}`, {
        title: data.title,
        slug: data.slug,
        excerpt: data.excerpt,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['post', post.id.toString(), locale],
      })
      queryClient.invalidateQueries({
        queryKey: ['posts', locale],
      })
      setShowModal(false)
      setTitle('')
      setSlug('')
      setExcerpt('')
      setError('')
      slugManuallyEdited.current = false
      if (onTranslationCreated) {
        onTranslationCreated()
      }
    },
    onError: (err) => {
      const message =
        err.response?.data?.message || 'Failed to create translation'
      setError(message)
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!title.trim() || !slug.trim()) {
      setError('Title and slug are required')
      return
    }

    createTranslationMutation.mutate({
      title: title.trim(),
      slug: slug.trim(),
      excerpt: excerpt.trim(),
    })
  }

  return (
    <>
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm text-blue-700">
              No <strong>{languageName}</strong> translation exists for this
              post. Content is visible but read-only until you add a
              translation.
            </p>
          </div>
          <div className="ml-4">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowModal(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add {languageName} Translation
            </Button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Add {languageName} Translation
              </h3>
              <button
                onClick={() => {
                  setShowModal(false)
                  setError('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <Input
                    type="text"
                    value={title}
                    onChange={(e) => {
                      const value = e.target.value
                      setTitle(value)
                      if (!slugManuallyEdited.current) {
                        setSlug(slugify(value))
                      }
                    }}
                    placeholder="Enter post title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug
                  </label>
                  <Input
                    type="text"
                    value={slug}
                    onChange={(e) => {
                      slugManuallyEdited.current = true
                      setSlug(e.target.value)
                    }}
                    placeholder="enter-post-slug"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    URL-friendly identifier for this post
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Excerpt
                  </label>
                  <textarea
                    value={excerpt}
                    onChange={(e) => setExcerpt(e.target.value)}
                    placeholder="Short description"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="3"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowModal(false)
                    setError('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={createTranslationMutation.isPending}
                >
                  {createTranslationMutation.isPending
                    ? 'Creating...'
                    : 'Create Translation'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
